import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import { PageRequest } from '@/core/request/PageRequest';

import {
  Recommendation,
  RecommendationCreateRequest,
  RecommendationListQuery,
  RecommendationUpdateRequest,
} from './Recommendation.types';

import {
  createRecommendation,
  deleteRecommendation,
  getRecommendation,
  listRecommendations,
  updateRecommendation,
} from './Recommendation.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { message } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';

export function useRecommendations(
  params?: RecommendationListQuery,
  enabled = true,
) {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryData(['recommendations', { ...params }]);

  const query = useInfiniteQuery(
    ['recommendations', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listRecommendations(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      enabled: enabled && !cache,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useCreateRecommendation() {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const mutation = useMutation(
    (obj: RecommendationCreateRequest) => createRecommendation(obj),
    {
      onSuccess() {
        message.success(
          intl.formatMessage({
            id: 'customers.form.recommendation.create.success',
          }),
        );
        queryClient.removeQueries(['recommendations']);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useRecommendationDetail(recommendationId?: number) {
  const query = useQuery(
    ['recommendation/detail', recommendationId],
    () => getRecommendation(recommendationId),
    {
      enabled: !!recommendationId,

      onError(error) {
        notificationError(error);
      },
    },
  );

  return query;
}

export function useRecommendation() {
  const queryCLient = useQueryClient();

  return useMutation(
    async (id: number) => {
      const cache = queryCLient.getQueryData<Recommendation>([
        'recommendation/detail',
        id,
      ]);
      if (cache?.id === id) return cache;

      const data = await getRecommendation(id);
      return data;
    },
    {
      onSuccess(data) {
        queryCLient.setQueryData(['recommendation/detail', data.id], data);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useUpdateRecommendation() {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (obj: RecommendationUpdateRequest) => updateRecommendation(obj),
    {
      onSuccess(data) {
        message.success(
          intl.formatMessage({
            id: 'customers.form.recommendation.update.success',
          }),
        );
        queryClient.removeQueries(['recommendations']);
        queryClient.removeQueries(['recommendation/detail', data.id]);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useRemoveRecommendation() {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const mutation = useMutation(
    (recommendationId: number) => deleteRecommendation(recommendationId),
    {
      onSuccess() {
        message.success(
          intl.formatMessage({
            id: 'customers.form.recommendation.delete.success',
          }),
        );
        queryClient.removeQueries(['recommendations']);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}
